import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from './button';

type ButtonProps = React.ComponentProps<typeof Button>;

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 z-modal bg-overlay duration-normal ease-standard data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
        className
      )}
      {...props}
    />
  );
}
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  hideClose?: boolean;
  overlayClassName?: string;
  closeClassName?: string;
  /**
   * Centers the corner X on the title's line (a `DialogTitle` at the top: 28px line, below the 16px
   * padding). It is also the default now, so this only restates it.
   */
  alignCloseWithTitle?: boolean;
};

const CLOSE_ON_TITLE_LINE = 'top-[18px]';

/**
 * Radix focuses the first field on open and selects its text, so a pre-filled edit form opens with its
 * value highlighted (one keystroke replaces it). Keep the focus but put the caret at the end instead.
 */
const keepFocusedTextUnselected = () => {
  const field = document.activeElement;
  if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;
  // `selectionStart` is null on inputs without a caret (number, date, checkbox, ...).
  if (field.selectionStart === null) return;
  field.setSelectionRange(field.value.length, field.value.length);
};

function DialogContent({
  className,
  children,
  hideClose = false,
  overlayClassName,
  closeClassName,
  alignCloseWithTitle = false,
  onOpenAutoFocus,
  ...props
}: DialogContentProps) {
  return (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        onOpenAutoFocus={(event) => {
          onOpenAutoFocus?.(event);
          // Radix focuses (and selects) right after this handler, unless it was prevented.
          if (!event.defaultPrevented) queueMicrotask(keepFocusedTextUnselected);
        }}
        onInteractOutside={(e) => {
          const evt = (e as unknown as any).detail.originalEvent;
          evt.preventDefault();
          evt.stopPropagation();
        }}
        className={cn(
          'fixed inset-0 z-modal m-auto grid h-fit max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-[560px] grid-cols-[minmax(0,1fr)] origin-center gap-4 overflow-y-auto rounded-lg border bg-popover p-4 text-popover-foreground shadow-lg outline-none duration-normal ease-standard data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          // Centred with `inset-0 m-auto h-fit`, not a -50% translate: the open/close animation sets
          // `transform` too, which dropped the translate and made dialogs slide in from the right.
          // Now they zoom out of and back into the centre of the screen (user, 3 Nov 2026).
          // On phones a centred card 16px from the screen edges, not full screen (user, 3 Nov 2026,
          // overriding the Component Map's "full screen on phones"); it scrolls inside when tall.
          // Prototype `.dialog`: 560px wide, 16px sections (head, body, footer).
          className
        )}
        {...props}
        aria-describedby={undefined}
      >
        {children}
        {!hideClose && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className={cn(
              'absolute right-3 top-[18px] inline-flex size-6 items-center justify-center rounded-sm text-muted-foreground transition-colors duration-fast ease-standard hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:text-disabled-foreground',
              alignCloseWithTitle && CLOSE_ON_TITLE_LINE,
              closeClassName
            )}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 pr-6 text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      // Prototype `.dialog-foot`: a full-width top border (the negative margins cancel the content's
      // 16px padding), 12px above and 16px below the buttons.
      '-mx-4 -mb-4 flex flex-col-reverse gap-2 border-t px-4 pb-4 pt-3 sm:flex-row sm:justify-end sm:gap-0 sm:space-x-2',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

/**
 * A dialog's Cancel button. Hidden on a phone (below 768px), where the corner X closes the dialog and
 * the footer keeps only the full-width primary action, as in the old portal. Don't use it in a dialog
 * with `hideClose`, which would then have no way out on a phone.
 */
const DialogCancelButton = ({ className, ...props }: ButtonProps) => (
  <Button
    type="button"
    variant="outline"
    data-dialog-cancel=""
    className={cn('max-md:hidden', className)}
    {...props}
  />
);
DialogCancelButton.displayName = 'DialogCancelButton';

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('break-words text-lg font-semibold leading-7', className)}
      {...props}
    />
  );
}
DialogTitle.displayName = DialogPrimitive.Title.displayName;

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogCancelButton,
  DialogTitle,
  DialogDescription,
};
